import { AbstractService } from './abstract-service';
import { inject, Injectable } from '@angular/core';
import { PreferenceService } from './preference.service';
import { SolValidationMessage } from '../models/core/sol-validation-message';
import { PreferenceTools } from '../tools/preference-tools';
import { SolSettingKey } from '../models/core/sol-setting-key';
import { EnumTools } from '../tools/enum-tools';

export interface ThemeState {
  current?: Theme;
  defaultTheme: Theme;
  themes: ThemeInfo[];
  overridden: boolean;
  saveFailure?: SolValidationMessage;
}

export enum Theme {
  SOL_LIGHT = 'sol-light',
  SOL_DARK = 'sol-dark',
  ASM_LIGHT = 'asm-light',
}

export interface ThemeInfo {
  id: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService extends AbstractService<ThemeState> {
  private static readonly NAME_KEY_PREFIX = 'sol-theme.';
  private static readonly LEGACY_STORAGE_KEY = 'theme';

  // Dependencies
  private readonly preferenceService = inject(PreferenceService);

  constructor() {
    super({
      defaultTheme: ThemeService.toSystemTheme(),
      themes: ThemeService.toBuiltInThemes(),
      overridden: false,
    });
  }

  public initialize(defaultTheme: Theme | undefined): void {
    const fallback = defaultTheme ?? ThemeService.toSystemTheme();
    const theme = this.toCachedTheme() ?? fallback;
    this.applyTheme(theme);
    this.patch({ current: theme, defaultTheme: fallback });
    this.reconcile();
  }

  /**
   * Set the theme for the application.
   */
  public setTheme(theme: Theme): void {
    const previous = this.state.current;
    this.applyTheme(theme);
    this.patch({ current: theme, saveFailure: undefined });
    this.preferenceService.save(SolSettingKey.theme, theme).subscribe({
      error: (error: unknown) => {
        if (previous !== undefined) {
          this.applyTheme(previous);
        }
        this.patch({ current: previous, saveFailure: PreferenceTools.toFailureMessage(error) });
      },
    });
  }

  /**
   * Check if the current theme is the same as the provided theme.
   */
  public isTheme(theme: Theme): boolean {
    return this.state.current === theme;
  }

  public overrideTheme(theme: Theme): void {
    this.applyTheme(theme);
    this.patch({ current: theme, overridden: true });
  }

  public setThemes(ids: readonly string[]): void {
    const themes = ids
      .filter(id => EnumTools.fromString(Theme, id) !== undefined)
      .map(id => ({ id, name: ThemeService.toNameKey(id) }));
    if (themes.length === 0) {
      return;
    }
    this.patch({ themes });
  }

  private static toNameKey(id: string): string {
    return `${ThemeService.NAME_KEY_PREFIX}${id}`;
  }

  private reconcile(): void {
    this.preferenceService.prePainted().subscribe(values => {
      const theme = this.toAnswered(values[SolSettingKey.theme.name]);
      if (theme === undefined) {
        return;
      }
      // Caching the default too keeps the first frame of the next load of this app from flashing.
      this.preferenceService.cache(SolSettingKey.theme, theme);
      if (this.state.overridden || theme === this.state.current) {
        return;
      }
      this.applyTheme(theme);
      this.patch({ current: theme });
    });
  }

  /** An absent key expresses no opinion; a `null` one was asked and had no answer, so the default applies. */
  private toAnswered(answer: unknown): Theme | undefined {
    if (answer === undefined) {
      return undefined;
    }
    return answer === null ? this.state.defaultTheme : EnumTools.fromString(Theme, answer as string);
  }

  private toCachedTheme(): Theme | undefined {
    const cached = this.preferenceService.cached(SolSettingKey.theme);
    const legacy = this.localStorageService.get<string>(ThemeService.LEGACY_STORAGE_KEY);
    return EnumTools.fromString(Theme, cached ?? legacy);
  }

  /**
   * Apply the theme to the document
   */
  private applyTheme(theme: Theme): void {
    document.documentElement.classList.toggle('theme-sol-dark', theme === Theme.SOL_DARK);
    document.documentElement.classList.toggle('theme-sol-light', theme === Theme.SOL_LIGHT);
    document.documentElement.classList.toggle('theme-asm-light', theme === Theme.ASM_LIGHT);
  }

  private static toSystemTheme(): Theme {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? Theme.SOL_DARK : Theme.SOL_LIGHT;
  }

  private static toBuiltInThemes(): ThemeInfo[] {
    return [Theme.ASM_LIGHT, Theme.SOL_LIGHT, Theme.SOL_DARK].map(theme => ({
      id: theme as string,
      name: ThemeService.toNameKey(theme),
    }));
  }
}
